// Shared presentational primitives for the Tandem console.
import { Box, Typography } from "@mui/material";
import type { ReactNode } from "react";
import type { SxProps, Theme } from "@mui/material/styles";
import { T } from "./tokens";

// Small "Not saved" pill — marks controls whose changes stay in the browser.
export const LocalOnlyBadge = ({ title }: { title?: string }) => (
    <Box
        component="span"
        title={title ?? "Changes stay in this browser — nothing is written back"}
        sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            fontSize: "10px",
            fontWeight: 700,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: T.amber,
            background: T.amberTint,
            border: `1px solid ${T.amberBorder}`,
            borderRadius: "999px",
            padding: "2px 8px",
            whiteSpace: "nowrap",
        }}
    >
        ● Not saved
    </Box>
);

// Back-compat alias: the Report Writer screens still import `SampleBadge`.
// Tandem itself no longer uses sample data — see LocalOnlyBadge above.
export const SampleBadge = LocalOnlyBadge;

export const StatusDot = ({ color, size = 8 }: { color: string; size?: number }) => (
    <Box
        component="span"
        sx={{
            width: `${size}px`,
            height: `${size}px`,
            borderRadius: "50%",
            background: color,
            flexShrink: 0,
            display: "inline-block",
        }}
    />
);

export const Meter = ({ pct, color, track = T.borderSoft }: { pct: number; color: string; track?: string }) => (
    <Box sx={{ height: "4px", borderRadius: "999px", background: track, overflow: "hidden" }}>
        <Box sx={{ width: `${Math.max(0, Math.min(100, pct))}%`, height: "100%", background: color }} />
    </Box>
);

export const Pill = ({ children, bg, fg }: { children: ReactNode; bg: string; fg: string }) => (
    <Box
        component="span"
        sx={{
            display: "inline-block",
            fontSize: "11px",
            fontWeight: 700,
            color: fg,
            background: bg,
            borderRadius: "999px",
            padding: "2px 9px",
            whiteSpace: "nowrap",
        }}
    >
        {children}
    </Box>
);

// White card wrapper used by every Overview panel.
export const Panel = ({
    title,
    sub,
    localOnly,
    action,
    children,
    sx,
}: {
    title?: ReactNode;
    sub?: ReactNode;
    localOnly?: boolean;
    action?: ReactNode;
    children?: ReactNode;
    sx?: SxProps<Theme>;
}) => (
    <Box
        sx={[
            {
                background: T.card,
                border: `1px solid ${T.border}`,
                borderRadius: "12px",
                padding: "16px 18px",
            },
            ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
    >
        {(title || action || localOnly) && (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: "10px",
                    mb: sub ? "10px" : "12px",
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                        {title && (
                            <Typography sx={{ fontSize: "15px", fontWeight: 700, color: T.ink2 }}>
                                {title}
                            </Typography>
                        )}
                        {localOnly && <LocalOnlyBadge />}
                    </Box>
                    {sub && (
                        <Typography sx={{ fontSize: "12.5px", color: T.muted, mt: "2px" }}>{sub}</Typography>
                    )}
                </Box>
                {action}
            </Box>
        )}
        {children}
    </Box>
);
